import React from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { Modal, Skeleton } from '@mui/material';
import { Done, Close } from '@mui/icons-material';
import { ServiceError } from '@/types/service';
import { Button, Title } from '@/components/atoms/Modal';
import { ServiceErrorView } from '@/components/atoms/Error';
import styles from './Modal.module.scss';

export const StandardModal: React.FC<{
  title: string;
  onClose: () => void;
  onSubmit: () => void;
  submittable: boolean;
  body: React.ReactElement;
  containerStyle?: React.CSSProperties;
  submitLabel?: string;
  closeLabel?: string;
  ignoreCloseLabel?: boolean;
  ignoreSubmitLabel?: boolean;
  modalName: string;
  disableBackdropClose?: boolean;
}> = ({
  title,
  onClose,
  onSubmit,
  submittable,
  body,
  containerStyle,
  submitLabel,
  closeLabel,
  ignoreCloseLabel,
  ignoreSubmitLabel,
  modalName,
  disableBackdropClose
}) => {
  return (
    <Modal
      open={true}
      onClose={(_, reason) => {
        if (disableBackdropClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
          return;
        }
        onClose();
      }}
    >
      <div
        style={{ minWidth: 300, ...containerStyle }}
        className={styles.modalContainer}
        data-cy={`modal_${modalName} ${modalName}`}
      >
        <HeadingModal
          title={title}
          onClose={onClose}
        />
        <BodyModal body={body} />
        <FooterModal
          submittable={submittable}
          onClose={onClose}
          onSubmit={onSubmit}
          submitLabel={submitLabel}
          closeLabel={closeLabel}
          ignoreCloseLabel={ignoreCloseLabel}
          ignoreSubmitLabel={ignoreSubmitLabel}
        />
      </div>
    </Modal>
  );
};

export const CustomModal: React.FC<{
  body: React.ReactElement;
  onClose: () => void;
  modalName: string;
  containerStyle?: React.CSSProperties;
}> = ({ onClose, body, containerStyle, modalName }) => {
  return (
    <Modal
      open={true}
      onClose={onClose}
    >
      <div
        style={{ minWidth: 300, ...containerStyle }}
        className={styles.modalContainer}
        data-cy={`modal_custom_${modalName} ${modalName}`}
      >
        {body}
      </div>
    </Modal>
  );
};

export const NoFooterModal: React.FC<{
  title: string;
  body: React.ReactElement;
  onClose: () => void;
  modalName: string;
  containerStyle?: React.CSSProperties;
}> = ({ title, onClose, body, containerStyle, modalName }) => {
  return (
    <Modal
      open={true}
      onClose={onClose}
    >
      <div
        style={{ minWidth: 300, ...containerStyle }}
        className={clsx([styles.modalContainer, styles.modalContainerNoFooter])}
        data-cy={`modal_noFooter_${modalName} ${modalName}`}
      >
        <HeadingModal
          title={title}
          onClose={onClose}
        />
        <BodyModal body={body} />
      </div>
    </Modal>
  );
};

export const LoadingModal: React.FC<{
  title: string;
  onClose: () => void;
  height?: number;
  containerStyle?: React.CSSProperties;
}> = ({ title, onClose, height, containerStyle }) => {
  return (
    <Modal
      open={true}
      onClose={onClose}
    >
      <div
        style={{ minWidth: 300, ...containerStyle }}
        className={clsx([styles.modalContainer, styles.modalContainerNoFooter])}
      >
        <HeadingModal
          title={title}
          onClose={onClose}
        />
        <div
          data-cy={'modal-body'}
          className={styles.bodyContainer}
        >
          <Skeleton
            height={height || 100}
            variant="rounded"
          />
        </div>
      </div>
    </Modal>
  );
};

export const SuccessModal: React.FC<{
  title: string;
  message?: string;
  onClose: () => void;
  containerStyle?: React.CSSProperties;
}> = ({ title, message, onClose, containerStyle }) => {
  const { t } = useTranslation();
  return (
    <Modal
      open={true}
      onClose={onClose}
    >
      <div
        style={{ width: 500, ...containerStyle }}
        className={clsx([styles.modalContainer, styles.modalContainerNoFooter])}
      >
        <HeadingModal
          title={title}
          onClose={onClose}
        />
        <div
          data-cy={'modal-body'}
          className={styles.successContainer}
        >
          <div className={styles.successIcon}>
            <Done />
          </div>
          <div className={styles.successMessage}>{message || t('common.operation_success')}</div>
        </div>
      </div>
    </Modal>
  );
};

export const ErrorModal: React.FC<{
  title: string;
  error?: ServiceError;
  message?: string;
  onClose: () => void;
  containerStyle?: React.CSSProperties;
  useOriginMsg?: boolean;
}> = ({ title, error, message, onClose, containerStyle, useOriginMsg }) => {
  const { t } = useTranslation();
  const messageView = error ? (
    <ServiceErrorView
      error={error}
      useOriginMsg={useOriginMsg}
    />
  ) : (
    message || t('common.operation_error')
  );

  return (
    <Modal
      open={true}
      onClose={onClose}
    >
      <div
        style={{ width: 500, ...containerStyle }}
        className={clsx([styles.modalContainer, styles.modalContainerNoFooter])}
      >
        <HeadingModal
          title={title}
          onClose={onClose}
        />
        <div
          data-cy={'modal-body'}
          className={styles.errorContainer}
        >
          <div className={styles.errorIcon}>
            <Close />
          </div>
          <div className={styles.errorMessage}>{messageView}</div>
        </div>
      </div>
    </Modal>
  );
};

export const CopyModal: React.FC<{
  title: string;
  beforeInfo?: React.ReactElement;
  afterInfo?: React.ReactElement;
  textToCopy: string;
  onClose: () => void;
  containerStyle?: React.CSSProperties;
}> = ({ title, beforeInfo, afterInfo, textToCopy, onClose, containerStyle }) => {
  const { t } = useTranslation();
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
    } catch {}
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
    >
      <div
        style={{ width: 400, ...containerStyle }}
        className={styles.modalContainer}
      >
        <HeadingModal
          title={title}
          onClose={onClose}
        />
        <div
          data-cy={'modal-body'}
          className={styles.copyContainer}
        >
          {beforeInfo}
          <div className={styles.copyText}>
            <strong>{textToCopy}</strong>
            <Button
              label={t('common.copy')}
              onClick={copyToClipboard}
              disabled={false}
            />
          </div>
          {afterInfo}
        </div>
        <CloseFooterModal
          closeLabel={t('common.close').toLowerCase()}
          onClose={onClose}
        />
      </div>
    </Modal>
  );
};

export const HeadingModal: React.FC<{
  title: string;
  onClose: () => void;
}> = ({ title, onClose }) => {
  return (
    <div className={styles.headingContainer}>
      <Title label={title} />
      <button
        data-cy={'modal-close'}
        className={styles.closeButton}
        onClick={onClose}
      >
        <Close className={styles.closeIcon} />
      </button>
    </div>
  );
};

export const BodyModal: React.FC<{
  body: React.ReactElement;
}> = ({ body }) => {
  return (
    <div
      data-cy={'modal-body'}
      className={styles.bodyContainer}
    >
      {body}
    </div>
  );
};

export const LoadingBodyModal: React.FC<{
  height?: number;
}> = ({ height }) => {
  return (
    <div className={styles.bodyContainer}>
      <Skeleton
        height={height || 100}
        variant="rounded"
      />
    </div>
  );
};

export const FooterModal: React.FC<{
  submittable: boolean;
  onClose: () => void;
  onSubmit: () => void;
  closeLabel?: string;
  submitLabel?: string;
  ignoreCloseLabel?: boolean;
  ignoreSubmitLabel?: boolean;
}> = ({ submittable, onClose, onSubmit, closeLabel, submitLabel, ignoreCloseLabel, ignoreSubmitLabel }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.footerContainer}>
      {!ignoreCloseLabel && (
        <Button
          label={closeLabel || t('common.cancel').toUpperCase()}
          onClick={onClose}
          disabled={false}
        />
      )}
      {!ignoreSubmitLabel && (
        <Button
          label={submitLabel || t('common.update').toUpperCase()}
          onClick={onSubmit}
          disabled={!submittable}
        />
      )}
    </div>
  );
};

export const CloseFooterModal: React.FC<{
  onClose: () => void;
  closeLabel?: string;
}> = ({ onClose, closeLabel }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.footerContainer}>
      <Button
        label={closeLabel || t('common.cancel').toUpperCase()}
        onClick={onClose}
        disabled={false}
      />
    </div>
  );
};

export const AddressFormModal: React.FC<{
  body: React.ReactElement;
  onClose: () => void;
}> = ({ onClose, body }) => {
  return (
    <Modal
      open={true}
      onClose={onClose}
    >
      <div className={styles.modalAddressForm}>
        <div className={styles.modalAddressFormHeader}>
          <button
            data-cy={'modal-close'}
            className={clsx([styles.modalAddressFormHeaderCloseButton, styles.closeButton])}
            onClick={onClose}
          >
            <Close className={styles.closeIcon} />
          </button>
        </div>
        <BodyModal body={body} />
      </div>
    </Modal>
  );
};
